<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import AppLayout from '../layouts/AppLayout.vue'
import { useAuth } from '../composables/useAuth'
import api from '../services/api'
import type { SupplierMetricConfig } from '../types/supplier'
import { useCurrentContext } from '../composables/useCurrentContext'

const { supplierId } = useCurrentContext()
const { getAccessTokenSilently } = useAuth()

const loading = ref(true)
const saving = ref(false)
const catalog = ref<SupplierMetricConfig[]>([])
const successMessage = ref('')

// Load catalog
async function loadCatalog() {
    try {
        loading.value = true
        const token = await getAccessTokenSilently()
        // Helper path: /api/suppliers/:id/metrics-catalog
        const res = await api.get(`/api/suppliers/${supplierId.value}/metrics-catalog`, {
            headers: { Authorization: `Bearer ${token}` }
        })
        catalog.value = res.data
    } catch (e) {
        console.error(e)
    } finally {
        loading.value = false
    }
}

// Save preferences
async function saveChanges() {
    try {
        saving.value = true
        const token = await getAccessTokenSilently()
        // Map to simple payload: array of updates
        const payload = catalog.value.map(m => ({
            key: m.key,
            showOnDashboard: m.showOnDashboard,
            includeInReports: m.includeInReports
        }))
        
        await api.put(`/api/suppliers/${supplierId.value}/metrics-catalog`, payload, {
            headers: { Authorization: `Bearer ${token}` }
        })
        successMessage.value = 'Preferences saved successfully!'
        setTimeout(() => successMessage.value = '', 3000)
    } catch (e) {
        console.error(e)
        alert('Failed to save changes')
    } finally {
        saving.value = false
    }
}

// Group by theme
const groupedMetrics = computed(() => {
    const groups: Record<string, SupplierMetricConfig[]> = {}
    catalog.value.forEach(m => {
        if (!groups[m.theme]) groups[m.theme] = []
        groups[m.theme]!.push(m)
    })
    return groups
})

onMounted(() => {
    loadCatalog()
})

const dashboardSelectedCount = computed(() => {
    return catalog.value.filter(m => m.showOnDashboard).length
})

const isDashboardLimitReached = computed(() => {
    return dashboardSelectedCount.value >= 8
})

function clearAll() {
    catalog.value.forEach(m => {
        m.showOnDashboard = false
        m.includeInReports = false
    })
}

const generateReport = () => {
    window.print()
}
</script>

<template>
  <AppLayout>
    <div class="space-y-6">
        <div class="flex justify-between items-center">
            <div>
                <h1 class="text-2xl font-bold text-cc-text">Metrics Catalog</h1>
                <p class="text-cc-text-secondary">
                    Customize your dashboard (<span :class="isDashboardLimitReached ? 'text-amber-600 font-medium' : ''">{{ dashboardSelectedCount }}/8 selected</span>) and reports.
                </p>
            </div>
            <div class="flex gap-3">
                <button @click="clearAll" class="px-4 py-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 border border-cc-border rounded-lg bg-cc-elevated hover:bg-red-50 dark:hover:bg-red-900/20">
                    Clear All
                </button>
                <router-link to="/supplier" class="px-4 py-2 text-cc-text-secondary hover:text-gray-900 dark:hover:text-white border border-cc-border rounded-lg bg-cc-elevated">
                    Cancel
                </router-link>
                <button @click="saveChanges" :disabled="saving" class="px-4 py-2 bg-cc-primary text-cc-btn-text rounded-lg hover:bg-cc-primary-hover disabled:opacity-50">
                    {{ saving ? 'Saving...' : 'Save Changes' }}
                </button>
                <button @click="generateReport" class="px-4 py-2 bg-cc-primary text-cc-btn-text rounded-lg hover:bg-cc-primary-hover ml-2">
                    Print Report
                </button>
            </div>
        </div>

        <div v-if="successMessage" class="bg-green-50 text-green-700 p-3 rounded border border-green-200">
            {{ successMessage }}
        </div>

        <div v-if="loading" class="text-gray-500 text-center py-10">Loading catalog...</div>

        <div v-else class="space-y-8">
            <div v-for="(items, theme) in groupedMetrics" :key="theme" class="bg-cc-elevated rounded-lg shadow-sm border border-cc-border overflow-hidden">
                <div class="px-6 py-3 bg-cc-deep border-b border-cc-border font-semibold text-cc-text-secondary">
                    {{ theme }}
                </div>
                <div class="divide-y divide-cc-border">
                    <div v-for="metric in items" :key="metric.key" class="p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/30">
                        <div>
                            <div class="font-medium text-cc-text">{{ metric.name }}</div>
                            <div class="text-sm text-cc-text-secondary">{{ metric.description }}</div>
                        </div>
                        <div class="flex items-center gap-6">
                            <label class="flex items-center gap-2 cursor-pointer" :class="{ 'opacity-50 cursor-not-allowed': isDashboardLimitReached && !metric.showOnDashboard }">
                                <input type="checkbox" v-model="metric.showOnDashboard" 
                                    :disabled="isDashboardLimitReached && !metric.showOnDashboard"
                                    class="rounded text-blue-600 focus:ring-cc-primary w-4 h-4 dark:bg-gray-700 dark:border-gray-600 disabled:opacity-50" />
                                <span class="text-sm text-cc-text-secondary">Dashboard</span>
                            </label>
                            <label class="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" v-model="metric.includeInReports" class="rounded text-indigo-600 focus:ring-indigo-500 w-4 h-4 dark:bg-gray-700 dark:border-gray-600" />
                                <span class="text-sm text-cc-text-secondary">Report</span>
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
  </AppLayout>
</template>
